import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Bell, Heart, MessageCircle, UserPlus, Share2, Check, Tag, BellRing } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'like': return <Heart className="h-4 w-4 text-red-500" />;
    case 'comment': return <MessageCircle className="h-4 w-4 text-blue-500" />;
    case 'friend_request': return <UserPlus className="h-4 w-4 text-green-500" />;
    case 'friend_accepted': return <Check className="h-4 w-4 text-green-600" />;
    case 'post_share': return <Share2 className="h-4 w-4 text-purple-500" />;
    case 'tag': return <Tag className="h-4 w-4 text-orange-500" />;
    default: return <Bell className="h-4 w-4 text-gray-500" />;
  }
};

const NotificationsPanel = () => {
  const { token } = useAuth();
  const { permission, isSubscribed, subscribe, unsubscribe } = usePushNotifications(token);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all read:', error);
    }
  };

  const handlePushToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
      toast({ title: 'Push notifications disabled' });
    } else {
      const ok = await subscribe();
      if (ok) {
        toast({ title: 'Push notifications enabled!', description: "You'll get notified on likes, comments, tags, and more." });
      } else if (permission === 'denied') {
        toast({ title: 'Notifications blocked', description: 'Enable notifications in your browser settings.', variant: 'destructive' });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="notifications-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
        </h2>
        {notifications.some(n => !n.read) && (
          <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
            Mark all read
          </Button>
        )}
      </div>

      {/* Push Notification Toggle */}
      <Card data-testid="push-notification-toggle">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isSubscribed ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <BellRing className={`h-5 w-5 ${isSubscribed ? 'text-blue-600' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="font-medium text-sm">Push Notifications</p>
                <p className="text-xs text-gray-500">
                  {isSubscribed ? 'You\'ll receive alerts even when the app is closed' : 'Enable to get notified on your device'}
                </p>
              </div>
            </div>
            <Switch
              checked={isSubscribed}
              onCheckedChange={handlePushToggle}
              data-testid="push-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No notifications yet</p>
            <p className="text-sm text-gray-400 mt-1">When someone likes, comments, tags, or shares your posts, you'll see it here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notif) => (
            <Card
              key={notif.id}
              className={`transition-colors ${!notif.read ? 'bg-blue-50 border-blue-200' : ''}`}
              data-testid={`notification-${notif.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={notif.from_profile_image} />
                    <AvatarFallback>{notif.from_username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <NotificationIcon type={notif.type} />
                      <p className="text-sm font-medium">{notif.content}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {!notif.read && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
